import { Component, OnInit, signal, computed, ChangeDetectorRef, ViewChild, ViewEncapsulation } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { WorkflowService } from "./service/workflow.service";
import { Workflow } from "./service/Workflow";
import { WorkflowUtil } from "./workflow.util";
import { S25Util } from "../../util/s25-util";
import { WorkflowStagesComponent } from "./workflow.stages.component";
import { WorkflowStageTransitionsComponent } from "./workflow.stage.transitions.component";
import { WorkflowStageRulesStepComponent } from "./workflow.stage.rules.step.component";
import { WorkflowStatesStepComponent } from "./workflow.states.step.component";
import { WorkflowStateRulesStepComponent } from "./workflow.state.rules.step.component";
import { WorkflowActionsStepComponent } from "./workflow.actions.step.component";
import { WorkflowActionRulesStepComponent } from "./workflow.action.rules.step.component";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { Table } from "../s25-table/Table";
import { S25TableModule } from "../s25-table/s25.table.module";
import { GenericTableButtonComponent } from "../s25-table/generics/generic.table.button.component";
import { S25TableComponent } from "../s25-table/s25.table.component";

@TypeManagerDecorator("workflow-processes-page")
@Component({
    selector: "workflow-processes-page",
    template: `
        <div class="workflow-page">
            @if (!selectedWorkflow()) {
                <!-- List View -->
                <div class="list-view">
                    <div class="header">
                        <h1>Workflows</h1>
                        <button class="btn btn-primary" (click)="createNewWorkflow()">New Workflow</button>
                    </div>
                    <s25-ng-table
                        #workflowTable
                        [dataSource]="tableConfig"
                        [columnSortable]="true"
                        [hasFilter]="true"
                        [hasRefresh]="true"
                    ></s25-ng-table>
                </div>
            } @else {
                <!-- Edit View -->
                <div class="edit-view">
                    <div class="header">
                        <h1>{{ isNewWorkflow() ? "New Workflow" : selectedWorkflow()!.workflowProcessName }}</h1>
                        <div class="actions">
                            <button class="btn btn-secondary" (click)="cancelEdit()">Cancel</button>
                            <button class="btn btn-primary" (click)="saveWorkflow()">
                                {{ isNewWorkflow() ? "Create" : "Save" }}
                            </button>
                        </div>
                    </div>

                    <div class="workflow-steps">
                        <!-- Step 1: Basic Info -->
                        <div class="step" [class.active]="currentStep() === 1">
                            <div class="step-header" (click)="toggleStep(1)">
                                <span class="step-number">1</span>
                                <span class="step-title">Basic Info</span>
                                <span class="step-status" [class.complete]="isStepComplete(1)">✓</span>
                                <span class="step-arrow" [class.expanded]="currentStep() === 1">▶</span>
                            </div>
                            @if (currentStep() === 1) {
                                <div class="step-content">
                                    <div class="form-row">
                                        <label>Name *</label>
                                        <input
                                            type="text"
                                            [(ngModel)]="selectedWorkflow()!.workflowProcessName"
                                            placeholder="Enter workflow name"
                                        />
                                    </div>
                                    <div class="form-row">
                                        <label>Description</label>
                                        <textarea
                                            [(ngModel)]="selectedWorkflow()!.description"
                                            placeholder="Optional description"
                                            rows="2"
                                        ></textarea>
                                    </div>
                                </div>
                            }
                        </div>

                        <!-- Step 2: Stages -->
                        <div class="step" [class.active]="currentStep() === 2">
                            <div class="step-header" (click)="toggleStep(2)">
                                <span class="step-number">2</span>
                                <span class="step-title">Stages</span>
                                <span class="step-status" [class.complete]="isStepComplete(2)">✓</span>
                                <span class="step-arrow" [class.expanded]="currentStep() === 2">▶</span>
                            </div>
                            @if (currentStep() === 2) {
                                <div class="step-content">
                                    <workflow-stages
                                        [workflowProcess]="selectedWorkflow()!"
                                        (workflowProcessChange)="onWorkflowProcessChange($event)"
                                    ></workflow-stages>
                                </div>
                            }
                        </div>

                        <!-- Step 3: Stage Rules -->
                        <div class="step" [class.active]="currentStep() === 3">
                            <div class="step-header" (click)="toggleStep(3)">
                                <span class="step-number">3</span>
                                <span class="step-title">Stage Rules</span>
                                <span class="step-status" [class.complete]="isStepComplete(3)">✓</span>
                                <span class="step-arrow" [class.expanded]="currentStep() === 3">▶</span>
                            </div>
                            @if (currentStep() === 3) {
                                <div class="step-content">
                                    <workflow-stage-rules-step
                                        [workflowProcess]="selectedWorkflow()!"
                                    ></workflow-stage-rules-step>
                                </div>
                            }
                        </div>

                        <!-- Step 4: States -->
                        <div class="step" [class.active]="currentStep() === 4">
                            <div class="step-header" (click)="toggleStep(4)">
                                <span class="step-number">4</span>
                                <span class="step-title">States</span>
                                <span class="step-status" [class.complete]="isStepComplete(4)">✓</span>
                                <span class="step-arrow" [class.expanded]="currentStep() === 4">▶</span>
                            </div>
                            @if (currentStep() === 4) {
                                <div class="step-content">
                                    <workflow-states-step
                                        [workflowProcess]="selectedWorkflow()!"
                                        (workflowProcessChange)="onWorkflowProcessChange($event)"
                                    ></workflow-states-step>
                                </div>
                            }
                        </div>

                        <!-- Step 5: State Rules -->
                        <div class="step" [class.active]="currentStep() === 5">
                            <div class="step-header" (click)="toggleStep(5)">
                                <span class="step-number">5</span>
                                <span class="step-title">State Rules</span>
                                <span class="step-status" [class.complete]="isStepComplete(5)">✓</span>
                                <span class="step-arrow" [class.expanded]="currentStep() === 5">▶</span>
                            </div>
                            @if (currentStep() === 5) {
                                <div class="step-content">
                                    <workflow-state-rules-step
                                        [workflowProcess]="selectedWorkflow()!"
                                    ></workflow-state-rules-step>
                                </div>
                            }
                        </div>

                        <!-- Step 6: Actions -->
                        <div class="step" [class.active]="currentStep() === 6">
                            <div class="step-header" (click)="toggleStep(6)">
                                <span class="step-number">6</span>
                                <span class="step-title">Actions</span>
                                <span class="step-status" [class.complete]="isStepComplete(6)">✓</span>
                                <span class="step-arrow" [class.expanded]="currentStep() === 6">▶</span>
                            </div>
                            @if (currentStep() === 6) {
                                <div class="step-content">
                                    <workflow-actions-step
                                        [workflowProcess]="selectedWorkflow()!"
                                        (workflowProcessChange)="onWorkflowProcessChange($event)"
                                    ></workflow-actions-step>
                                </div>
                            }
                        </div>

                        <!-- Step 7: Action Rules -->
                        <div class="step" [class.active]="currentStep() === 7">
                            <div class="step-header" (click)="toggleStep(7)">
                                <span class="step-number">7</span>
                                <span class="step-title">Action Rules</span>
                                <span class="step-status" [class.complete]="isStepComplete(7)">✓</span>
                                <span class="step-arrow" [class.expanded]="currentStep() === 7">▶</span>
                            </div>
                            @if (currentStep() === 7) {
                                <div class="step-content">
                                    <workflow-action-rules-step
                                        [workflowProcess]="selectedWorkflow()!"
                                    ></workflow-action-rules-step>
                                </div>
                            }
                        </div>

                        <!-- Step 8: Transitions -->
                        <div class="step" [class.active]="currentStep() === 8">
                            <div class="step-header" (click)="toggleStep(8)">
                                <span class="step-number">8</span>
                                <span class="step-title">Transitions</span>
                                <span class="step-status" [class.complete]="isStepComplete(8)">✓</span>
                                <span class="step-arrow" [class.expanded]="currentStep() === 8">▶</span>
                            </div>
                            @if (currentStep() === 8) {
                                <div class="step-content">
                                    <workflow-stage-transitions
                                        [workflowProcess]="selectedWorkflow()!"
                                        (transitionsChange)="onStageTransitionsChange($event)"
                                    ></workflow-stage-transitions>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        .workflow-page {
            height: 100vh;
            display: flex;
            flex-direction: column;
        }

        .header {
            padding: 20px;
            background: white;
            border-bottom: 1px solid #e0e0e0;
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .header h1 {
            margin: 0;
            color: #333;
            font-size: 24px;
        }

        .actions {
            display: flex;
            gap: 10px;
        }

        .btn {
            padding: 8px 16px;
            border: 1px solid #ddd;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            transition: all 0.2s;
        }

        .btn-primary {
            background: #007bff;
            color: white;
            border-color: #007bff;
        }

        .btn-primary:hover {
            background: #0056b3;
        }

        .btn-secondary {
            background: white;
            color: #666;
        }

        .btn-secondary:hover {
            background: #f8f9fa;
        }

        .list-view {
            flex: 1;
            padding: 20px;
        }

        .edit-view {
            flex: 1;
            display: flex;
            flex-direction: column;
        }

        .workflow-steps {
            flex: 1;
            padding: 20px;
            display: flex;
            flex-direction: column;
            gap: 20px;
        }

        .step {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
        }

        .step.active {
            border-color: #007bff;
            box-shadow: 0 0 0 1px #007bff;
        }

        .step-header {
            padding: 16px 20px;
            background: #f8f9fa;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 12px;
            transition: background 0.2s;
        }

        .step-header:hover {
            background: #e9ecef;
        }

        .step-number {
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: #ddd;
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: bold;
        }

        .step.active .step-number {
            background: #007bff;
        }

        .step-title {
            flex: 1;
            font-weight: 500;
            color: #333;
        }

        .step-status {
            opacity: 0;
            color: #28a745;
            font-weight: bold;
        }

        .step-status.complete {
            opacity: 1;
        }

        .step-arrow {
            margin-left: auto;
            color: #666;
            font-size: 12px;
            transition: transform 0.2s;
        }

        .step-arrow.expanded {
            transform: rotate(90deg);
        }

        .step-content {
            padding: 20px;
        }

        .form-row {
            margin-bottom: 16px;
        }

        .form-row label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-row input,
        .form-row textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            box-sizing: border-box;
        }

        .form-row input:focus,
        .form-row textarea:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }
    `,
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        WorkflowStagesComponent,
        WorkflowStageTransitionsComponent,
        WorkflowStageRulesStepComponent,
        WorkflowStatesStepComponent,
        WorkflowStateRulesStepComponent,
        WorkflowActionsStepComponent,
        WorkflowActionRulesStepComponent,
        S25DropdownPaginatedModule,
        S25TableModule,
    ],
    encapsulation: ViewEncapsulation.Emulated,
})
export class WorkflowProcessesPageComponent implements OnInit {
    @ViewChild("workflowTable")
    tableComponent!: S25TableComponent;

    selectedWorkflowId = signal<number | null>(null);
    selectedWorkflow = signal<Workflow.WorkflowProcess | null>(null);
    currentStep = signal<number>(1);
    tableConfig: Table.DataSource;

    constructor(private cd: ChangeDetectorRef) {}

    isNewWorkflow = computed(() => !this.selectedWorkflowId());

    ngOnInit() {
        this.initializeTableConfig();
    }

    private initializeTableConfig() {
        const columns: Table.Column[] = [
            { id: "name", header: "Name" },
            { id: "description", header: "Description" },
            { id: "stages", header: "Stages" },
            { id: "stageTransitions", header: "Stage Transitions" },
            GenericTableButtonComponent.Column("Edit", this.onEditClick.bind(this), "outline"),
            GenericTableButtonComponent.Column("Copy", this.onCopyClick.bind(this), "outline"),
            GenericTableButtonComponent.Column("Delete", this.onDeleteClick.bind(this), "danger--outline"),
        ];

        this.tableConfig = {
            type: "unpaginated",
            columns: columns,
            dataSource: this.getWorkflowProcessesData.bind(this),
        };
    }

    async refresh() {
        // Only refresh table if we're in list mode and the component is available
        if (!this.selectedWorkflow() && this.tableComponent) {
            this.tableComponent.refresh();
        }
        this.cd.detectChanges();
    }

    async getWorkflowProcessesData(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        const processes = await WorkflowService.getWorkflowProcesses();
        const rows: Table.Row[] = processes.map((process) => ({
            id: process.workflowProcessId,
            name: process.workflowProcessName,
            cells: {
                name: { text: process.workflowProcessName },
                description: { text: process.description || "No description" },
                stages: { text: process.stages?.length || 0 },
                stageTransitions: { text: process.stageTransitions?.length || 0 },
            },
            data: process,
        }));
        return {
            rows: rows,
            totalRows: rows.length,
        };
    }

    async onEditClick(row: Table.Row) {
        const workflow = row.data as Workflow.WorkflowProcess;
        await this.selectWorkflow(workflow);
    }

    async onDeleteClick(row: Table.Row) {
        const workflowId = row.id as number;
        await this.deleteWorkflow(workflowId);
    }

    async onCopyClick(row: Table.Row) {
        const workflow = row.data as Workflow.WorkflowProcess;
        await this.copyWorkflow(workflow);
    }

    async selectWorkflow(workflow: Workflow.WorkflowProcess) {
        this.selectedWorkflowId.set(workflow.workflowProcessId!);
        this.selectedWorkflow.set(S25Util.deepCopy(workflow));
        this.currentStep.set(1);

        // Ensure states and transitions arrays exist
        if (!this.selectedWorkflow()!.stages) {
            this.selectedWorkflow()!.stages = [];
        }
        if (!this.selectedWorkflow()!.stageTransitions) {
            this.selectedWorkflow()!.stageTransitions = [];
        }

        this.cd.detectChanges();
    }

    createNewWorkflow() {
        this.selectedWorkflowId.set(null);
        this.selectedWorkflow.set({
            workflowProcessName: "",
            description: "",
            enabled: true,
            stages: [],
            stageTransitions: [],
        });
        this.currentStep.set(1);
        this.cd.detectChanges();
    }

    setCurrentStep(step: number) {
        this.currentStep.set(step);
        this.cd.detectChanges();
    }

    toggleStep(step: number) {
        if (this.currentStep() === step) {
            // If clicking on the currently active step, collapse it
            this.currentStep.set(0);
        } else {
            // Otherwise, expand the clicked step
            this.currentStep.set(step);
        }
        this.cd.detectChanges();
    }

    isStepComplete(step: number): boolean {
        const workflow = this.selectedWorkflow();
        if (!workflow) return false;

        switch (step) {
            case 1:
                return !!workflow.workflowProcessName?.trim();
            case 2:
                return !!(workflow.stages && workflow.stages.length > 0);
            case 3:
                // Stage rules - always complete if stages exist
                return !!(workflow.stages && workflow.stages.length > 0);
            case 4:
                // States - check if all stages have at least one state
                return !!(
                    workflow.stages &&
                    workflow.stages.length > 0 &&
                    workflow.stages.every((stage) => stage.states && stage.states.length > 0)
                );
            case 5:
                // State rules - always complete if states exist
                return !!(
                    workflow.stages &&
                    workflow.stages.length > 0 &&
                    workflow.stages.every((stage) => stage.states && stage.states.length > 0)
                );
            case 6:
                // Actions - always complete if stages exist
                return !!(workflow.stages && workflow.stages.length > 0);
            case 7:
                // Action rules - always complete if actions exist
                return !!(
                    workflow.stages &&
                    workflow.stages.length > 0 &&
                    workflow.stages.some((stage) => stage.actions && stage.actions.length > 0)
                );
            case 8:
                return !!(workflow.stageTransitions && workflow.stageTransitions.length > 0);
            default:
                return false;
        }
    }

    async copyWorkflow(originalWorkflow: Workflow.WorkflowProcess) {
        // Create a deep copy of the workflow
        const copiedWorkflow = S25Util.deepCopy(originalWorkflow);

        // Clear all IDs to ensure they get new ones when saved
        copiedWorkflow.workflowProcessId = undefined;
        copiedWorkflow.workflowProcessName = `${originalWorkflow.workflowProcessName} (Copy)`;

        // Clear stage IDs and reset to new workflow
        if (copiedWorkflow.stages) {
            copiedWorkflow.stages.forEach((stage, stageIndex) => {
                stage.workflowStageId = undefined;
                stage.workflowProcessId = undefined;
                stage.id = -(stageIndex + 1); // Use negative IDs for new stages

                // Clear state IDs
                if (stage.states) {
                    stage.states.forEach((state, stateIndex) => {
                        state.workflowStateId = undefined;
                        state.workflowStageId = undefined;
                        state.workflowProcessId = undefined;
                        state.id = -(stateIndex + 1); // Use negative IDs for new states

                        // Clear action IDs
                        if (stage.actions) {
                            stage.actions.forEach((action, actionIndex) => {
                                action.workflowActionId = undefined;
                                action.workflowStageId = undefined;
                                action.id = -(actionIndex + 1); // Use negative IDs for new actions

                                // Clear action input IDs
                                if (action.inputs) {
                                    action.inputs.forEach((input, inputIndex) => {
                                        input.id = -(inputIndex + 1); // Use negative IDs for new inputs
                                    });
                                }
                            });
                        }
                    });
                }
            });
        }

        // Clear transition IDs
        if (copiedWorkflow.stageTransitions) {
            copiedWorkflow.stageTransitions.forEach((transition, transitionIndex) => {
                transition.workflowStageTransitionId = undefined;
                transition.workflowProcessId = undefined;
                transition.id = -(transitionIndex + 1); // Use negative IDs for new transitions
            });
        }

        // Set up the copied workflow for editing
        this.selectedWorkflowId.set(null); // This makes it a new workflow
        this.selectedWorkflow.set(copiedWorkflow);

        this.cd.detectChanges();
    }

    cancelEdit() {
        this.selectedWorkflowId.set(null);
        this.selectedWorkflow.set(null);
        this.cd.detectChanges();
    }

    async saveWorkflow() {
        const workflow = this.selectedWorkflow();
        if (!workflow) return;

        if (!workflow.workflowProcessName.trim()) {
            S25Util.showError("Workflow name is required");
            return;
        }

        // Validate workflow structure using current state
        const validationErrors = this.validateWorkflowStructure(workflow);
        if (validationErrors.length > 0) {
            S25Util.showError("Workflow validation failed:\n" + validationErrors.join("\n"));
            return;
        }

        try {
            if (this.isNewWorkflow()) {
                await WorkflowService.createWorkflowProcess(workflow);
                console.log("Workflow created successfully");
            } else {
                await WorkflowService.updateWorkflowProcess(workflow.workflowProcessId!, workflow);
                console.log("Workflow updated successfully");
            }

            await this.refresh();
            this.cancelEdit();
        } catch (error) {
            console.error("Error saving workflow:", error);
            S25Util.showError(error);
        }
    }

    async deleteWorkflow(workflowId: number) {
        if (!confirm("Are you sure you want to delete this workflow? This action cannot be undone.")) {
            return;
        }

        try {
            await WorkflowService.deleteWorkflowProcess(workflowId);
            console.log("Workflow deleted successfully");
            await this.refresh();

            if (this.selectedWorkflowId() === workflowId) {
                this.cancelEdit();
            }
        } catch (error) {
            console.error("Error deleting workflow:", error);
            S25Util.showError(error);
        }
    }

    onWorkflowProcessChange(updatedWorkflow: Workflow.WorkflowProcess) {
        this.selectedWorkflow.set(updatedWorkflow);
        this.cd.detectChanges();
    }

    onStageTransitionsChange(transitions: Workflow.WorkflowStageTransition[]) {
        const workflow = this.selectedWorkflow();
        if (workflow) {
            workflow.stageTransitions = transitions;
        }
        this.cd.detectChanges();
    }

    private validateWorkflowStructure(workflow: Workflow.WorkflowProcess): string[] {
        const errors: string[] = [];
        const stages = workflow.stages || [];
        const stageTransitions = workflow.stageTransitions || [];

        // Ensure stage names are populated on transitions for validation
        WorkflowUtil.populateStageNamesOnTransitions(stageTransitions, stages);

        if (stages.length === 0) {
            errors.push("At least one stage is required");
            return errors;
        }

        // Check for duplicate stage names
        const stageNames = new Set<string>();
        const duplicateStageNames = new Set<string>();
        stages.forEach((stage) => {
            const trimmedName = stage.stageName?.trim();
            if (trimmedName) {
                if (stageNames.has(trimmedName)) {
                    duplicateStageNames.add(trimmedName);
                } else {
                    stageNames.add(trimmedName);
                }
            }
        });
        duplicateStageNames.forEach((name) => {
            errors.push(`Duplicate stage name: "${name}". Stage names must be unique.`);
        });

        // Check for duplicate stage transition labels
        const transitionLabels = new Set<string>();
        const duplicateTransitionLabels = new Set<string>();
        stageTransitions.forEach((transition) => {
            const trimmedLabel = transition.transitionLabel?.trim();
            if (trimmedLabel) {
                if (transitionLabels.has(trimmedLabel)) {
                    duplicateTransitionLabels.add(trimmedLabel);
                } else {
                    transitionLabels.add(trimmedLabel);
                }
            }
        });
        duplicateTransitionLabels.forEach((label) => {
            errors.push(`Duplicate stage transition label: "${label}". Stage transition labels must be unique.`);
        });

        // Must have exactly one start stage
        const startStages = stages.filter((s) => s.isStartStage);
        if (startStages.length === 0) {
            errors.push("Workflow must have exactly one start stage");
        } else if (startStages.length > 1) {
            errors.push("Workflow can only have one start stage");
        }

        // Must have at least one terminal stage
        const terminalStages = stages.filter((s) => s.isTerminal);
        if (terminalStages.length === 0) {
            errors.push("Workflow must have at least one terminal stage");
        }

        // Check for unreachable stages
        if (stageTransitions.length > 0) {
            const reachableStages = new Set<string>();
            // Find the start stage
            const startStage = stages.find((s) => s.isStartStage);
            if (startStage) {
                reachableStages.add(startStage.stageName);
            }

            // Add all stages reachable through transitions
            let changed = true;
            while (changed) {
                changed = false;
                stageTransitions.forEach((t) => {
                    if (reachableStages.has(t.fromStageName) && !reachableStages.has(t.toStageName)) {
                        reachableStages.add(t.toStageName);
                        changed = true;
                    }
                });
            }

            const unreachableStages = stages.filter((s) => !reachableStages.has(s.stageName));
            // Start stage is always reachable
            unreachableStages.forEach((s) => {
                if (!s.isStartStage) {
                    errors.push(`Stage "${s.stageName}" is unreachable`);
                }
            });
        }

        // Check for stages with no outgoing transitions (except terminal stages)
        const stagesWithOutgoing = new Set(stageTransitions.map((t) => t.fromStageName));
        stages.forEach((stage) => {
            if (!stage.isTerminal && !stagesWithOutgoing.has(stage.stageName)) {
                errors.push(`Stage "${stage.stageName}" has no outgoing transitions`);
            }
        });

        // Check that terminal stages have NO outgoing transitions
        stages.forEach((stage) => {
            if (stage.isTerminal && stagesWithOutgoing.has(stage.stageName)) {
                errors.push(`Terminal stage "${stage.stageName}" cannot have outgoing transitions`);
            }
        });

        // Check for cycles in the workflow
        if (WorkflowUtil.hasCycle(stageTransitions)) {
            errors.push("Workflow contains cycles. Stages must form a directed acyclic graph (DAG).");
        }

        // Validate FSM constraints: each stage can have at most one transition per type
        stageTransitions.forEach((transition) => {
            const fsmErrors = WorkflowUtil.validateFSMConstraints(stageTransitions, transition);
            errors.push(...fsmErrors);
        });

        // Validate states within each stage
        stages.forEach((stage) => {
            if (!stage.states || stage.states.length === 0) {
                return;
            } else {
                // Check for duplicate state names within the stage
                const stateNames = new Set<string>();
                stage.states.forEach((state) => {
                    const trimmedName = state.stateName?.trim();
                    if (trimmedName) {
                        if (stateNames.has(trimmedName)) {
                            errors.push(
                                `Duplicate state name: "${trimmedName}" in stage "${stage.stageName}". State names must be unique within a stage.`,
                            );
                        } else {
                            stateNames.add(trimmedName);
                        }
                    }
                });

                // Validate state contact assignments (XOR constraint)
                stage.states.forEach((state) => {
                    const hasContacts = state.contacts && state.contacts.length > 0;
                    const hasApprovalGroup = state.approvalGroupId;

                    if (hasContacts && hasApprovalGroup) {
                        errors.push(
                            `State "${state.stateName}" in stage "${stage.stageName}" cannot have both contacts and approval group. Use either contacts OR approval group, not both.`,
                        );
                    }
                });
            }
        });

        return errors;
    }
}
